import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Form, Alert, Spinner } from 'react-bootstrap';
import { getStorage, ref, uploadBytes, listAll, getDownloadURL, deleteObject } from 'firebase/storage';
import { FaDownload, FaTrash, FaUpload, FaFile, FaFilePdf, FaFileWord, FaFileExcel } from 'react-icons/fa';

const StudyMaterialsAdmin = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDescription, setFileDescription] = useState('');
  const [fileType, setFileType] = useState('free'); // 'free' or 'paid'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const storage = getStorage();

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Fetch both free and paid files
      const freeRef = ref(storage, 'downloads/free');
      const paidRef = ref(storage, 'downloads/paid');
      
      const [freeList, paidList] = await Promise.all([
        listAll(freeRef).catch(() => ({ items: [] })),
        listAll(paidRef).catch(() => ({ items: [] }))
      ]);
      
      const freeFiles = await Promise.all(
        freeList.items.map(async (item) => {
          const url = await getDownloadURL(item);
          const metadata = item._location.path_.split('_');
          return {
            name: item.name.replace(/^\d+_/, ''),
            url: url,
            path: item.fullPath,
            timestamp: parseInt(metadata[2]),
            type: 'free'
          };
        })
      );
      
      const paidFiles = await Promise.all(
        paidList.items.map(async (item) => {
          const url = await getDownloadURL(item);
          const metadata = item._location.path_.split('_');
          return {
            name: item.name.replace(/^\d+_/, ''),
            url: url,
            path: item.fullPath,
            timestamp: parseInt(metadata[2]),
            type: 'paid'
          };
        })
      );
      
      // Combine and sort files by timestamp (newest first)
      const allFiles = [...freeFiles, ...paidFiles];
      allFiles.sort((a, b) => b.timestamp - a.timestamp);
      setFiles(allFiles);
    } catch (error) {
      console.error('Fetch error:', error);
      setError(`Failed to fetch files: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      // Create a storage reference with timestamp prefix
      const timestamp = Date.now();
      const fileName = `${fileType}_${timestamp}_${selectedFile.name}`;
      const storageRef = ref(storage, `downloads/${fileType}/${fileName}`);
      
      // Upload file
      await uploadBytes(storageRef, selectedFile);
      
      setSuccess('File uploaded successfully!');
      setFileDescription('');
      setSelectedFile(null);
      setFileType('free');
      
      // Refresh file list
      await fetchFiles();
      
      // Clear form
      e.target.reset();
    } catch (error) {
      console.error('Upload error:', error);
      setError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filePath) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        setError('');
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
        setSuccess('File deleted successfully!');
        await fetchFiles();
      } catch (error) {
        console.error('Delete error:', error);
        setError(`Delete failed: ${error.message}`);
      }
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return <FaFilePdf size={20} color="#dc3545" />;
      case 'doc':
      case 'docx': return <FaFileWord size={20} color="#0d6efd" />;
      case 'xls':
      case 'xlsx': return <FaFileExcel size={20} color="#198754" />;
      default: return <FaFile size={20} color="#6c757d" />;
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4">
      <h2 className="mb-4">Study Materials Admin</h2>
      
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Upload New File</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleFileUpload}>
            <Form.Group className="mb-3">
              <Form.Label>Select File</Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                required
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>File Type</Form.Label>
                  <Form.Select
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                  >
                    <option value="free">Free</option>
                    <option value="paid">Paid</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Description (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={fileDescription}
                    onChange={(e) => setFileDescription(e.target.value)}
                    placeholder="Enter file description"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Button
              variant="primary"
              type="submit"
              disabled={!selectedFile || uploading}
              className="d-flex align-items-center"
            >
              {uploading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <FaUpload className="me-2" />
                  Upload File
                </>
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <h5 className="mb-0">Available Study Materials</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2">Loading files...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <p>No files available.</p>
            </div>
          ) : (
            <Row xs={1} md={2} lg={3} className="g-4">
              {files.map((file, index) => (
                <Col key={index}>
                  <Card className="h-100 shadow-sm">
                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-start mb-2">
                        {getFileIcon(file.name)}
                        <span className={`badge ms-auto ${file.type === 'free' ? 'bg-success' : 'bg-warning'}`}>
                          {file.type.toUpperCase()}
                        </span>
                      </div>
                      
                      <Card.Title className="fs-6 text-truncate" title={file.name}>
                        {file.name}
                      </Card.Title>
                      
                      {fileDescription && (
                        <Card.Text className="text-muted small">
                          {fileDescription}
                        </Card.Text>
                      )}
                      
                      <Card.Text className="text-muted small mt-auto">
                        Uploaded: {formatDate(file.timestamp)}
                      </Card.Text>
                      
                      <div className="d-flex justify-content-between mt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex align-items-center"
                        >
                          <FaDownload className="me-1" />
                          Download
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(file.path)}
                          className="d-flex align-items-center"
                        >
                          <FaTrash className="me-1" />
                          Delete
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default StudyMaterialsAdmin;